/**
 * Chat List Thunks
 *
 * Async thunks for fetching the paginated list of user's past chat sessions.
 */
import { createAsyncThunk } from '@reduxjs/toolkit';
import { fetchChatsList } from '../api/chatListApi';
import {
  setChatListLoading,
  setChatListLoadingMore,
  setChatList,
  appendChatList,
  setChatListError,
} from '../slices/chatListSlice';
import { logger } from '@/shared/utils/logger';
import { getJwtToken, setJwtToken } from '@/shared/utils/session';
import { initSessionApi } from '@/api/sessionApi';
import type { AppDispatch, RootState } from '@/store/store';

/**
 * Fetch the initial page of chat sessions for the sidebar.
 * Skips the fetch if the list is already loaded (prevents redundant calls on re-open).
 * If no JWT exists (e.g. sidebar opened before first message on blank chat),
 * calls init API to get a JWT for auth only — does NOT store the session ID
 * or update the URL so it won't interfere with the real session created
 * when the user sends their first message.
 *
 * @param force - When true, bypasses the isLoaded cache and re-fetches
 */
export const fetchChatsListThunk = createAsyncThunk<
  void,
  { force?: boolean } | void,
  { dispatch: AppDispatch; state: RootState; rejectValue: string }
>('chat/fetchChatsList', async (params, { dispatch, getState, rejectWithValue }) => {
  const force = params?.force ?? false;
  const { isLoaded, isLoading } = getState().chat.chatList;

  if (isLoading) return;
  if (isLoaded && !force) return;

  if (!getJwtToken()) {
    try {
      const response = await initSessionApi();
      setJwtToken(response.token);
    } catch {
      return rejectWithValue('Authentication required to load chat history');
    }
  }

  dispatch(setChatListLoading());

  try {
    const response = await fetchChatsList({ limit: 20 });

    if (response.responseStatus !== 'SUCCESS') {
      return rejectWithValue('Failed to fetch chats list');
    }

    dispatch(
      setChatList({
        chats: response.data.chats,
        hasMore: response.data.hasMore,
        nextCursor: response.data.nextCursor ?? null,
      }),
    );

    logger.debug('Chats list loaded', {
      count: response.data.chats.length,
      hasMore: response.data.hasMore,
    });

    return;
  } catch (error) {
    logger.error('Error fetching chats list:', error);
    const message = error instanceof Error ? error.message : 'Failed to fetch chats list';
    dispatch(setChatListError(message));
    return rejectWithValue(message);
  }
});

/**
 * Load the next page of chat sessions (pagination via cursor).
 * Only dispatches if hasMore is true and not already loading.
 */
export const loadMoreChatsListThunk = createAsyncThunk<
  void,
  void,
  { dispatch: AppDispatch; state: RootState; rejectValue: string }
>('chat/loadMoreChatsList', async (_, { dispatch, getState, rejectWithValue }) => {
  const { hasMore, nextCursor, isLoadingMore } = getState().chat.chatList;

  if (isLoadingMore || !hasMore || nextCursor === null) return;

  dispatch(setChatListLoadingMore());

  try {
    const response = await fetchChatsList({ limit: 10, cursor: nextCursor });

    if (response.responseStatus !== 'SUCCESS') {
      return rejectWithValue('Failed to load more chats');
    }

    dispatch(
      appendChatList({
        chats: response.data.chats,
        hasMore: response.data.hasMore,
        nextCursor: response.data.nextCursor ?? null,
      }),
    );

    logger.debug('More chats loaded', {
      count: response.data.chats.length,
      hasMore: response.data.hasMore,
    });

    return;
  } catch (error) {
    logger.error('Error loading more chats:', error);
    const message = error instanceof Error ? error.message : 'Failed to load more chats';
    dispatch(setChatListError(message));
    return rejectWithValue(message);
  }
});
